import React, { useCallback, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import useAuthStore from 'store/useAuthStore';
import { getMenuIdFromPath, isKnownRoute } from 'helpers/menuPermissionMap';
import { hasPermission } from 'helpers/permissionHelper';
import NotAuthorized from 'pages/not-autorized';
import NotFound from '../../pages/not-found';
import { ChatProvider } from '../../pages/chat/context/provider';
import Footer from './footer-main';
import SidebarV2 from './sidebar';
import TopNav from './header';

const resolveAuthUser = (reduxUser) => {
  if (reduxUser) return reduxUser;

  try {
    const stored = localStorage.getItem('Pulsalink-Admin-Auth-User');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const AppLayout = ({ request }) => {
  const [show, setShow] = useState(() => typeof window === 'undefined' || window.innerWidth >= 768);
  const location = useLocation();
  const authUser = useAuthStore();
  const { user } = authUser;

  const toggle = useCallback(() => setShow((isOpen) => !isOpen), []);
  const closeSidebar = useCallback(() => setShow(false), []);

  const checkAccess = (path) => {
    const currentUser = resolveAuthUser(user);
    if (!currentUser) return false;

    const menuId = getMenuIdFromPath(path);
    return hasPermission(currentUser, menuId, path);
  };

  const currentPath = location.pathname;
  const isChatPage = currentPath === '/chat' || currentPath === '/chat-guest';
  const isRouteKnown = isKnownRoute(currentPath);
  const hasAccess = checkAccess(currentPath);

  let mainContent;
  if (!isRouteKnown) {
    mainContent = <NotFound />;
  } else if (!hasAccess) {
    mainContent = <NotAuthorized />;
  } else {
    mainContent = <Outlet context={[{ request, authUser, user }]} />;
  }

  return (
    <ChatProvider>
      <div className='flex h-dvh min-h-0 w-full overflow-hidden bg-gray-100 text-sm'>
        <a
          href='#main-content'
          className='sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[1200] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:font-semibold focus:text-slate-900 focus:shadow-lg'
        >
          Lewati ke konten utama
        </a>

        <SidebarV2 onClose={closeSidebar} show={show} />

        <div
          id='mainPage'
          className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden transition-[margin] duration-300 ease-in-out ${
            show ? 'md:ml-64' : 'ml-0'
          }`}
        >
          <TopNav show={show} toggle={toggle} request={request} />

          <main
            id='main-content'
            tabIndex={-1}
            className={`flex min-h-0 min-w-0 flex-1 flex-col p-4 outline-none sm:p-5 lg:p-6 ${
              isChatPage ? 'overflow-hidden' : 'overflow-x-hidden overflow-y-auto'
            }`}
          >
            {mainContent}
          </main>

          <Footer />
        </div>
      </div>
    </ChatProvider>
  );
};

export default AppLayout;
